import type {ReactElement} from 'react';
import {useEffect} from 'react';
import React, {useRef, useState} from 'react';
import styles from './email-maker-show-mailing-lists-panel.module.scss';
import {Text, TextSize} from '@Components/text';
import {ClickableDiv} from '@Components/clickable-div';
import {Icon} from '@Components/icon-v2';
import {FlatIconColor, FlatIconColorType, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {MailingList} from '@Components/email-marketing-campaign-performance/email-marketing-campaign-performance.types';
import type {ContainerQueryObject} from '@Hooks/useContainerQuery';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {deleteCookie, readCookie} from '@Utils/cookie.util';
import type {MailingListFetchResponse, WizardMailingListsFetchResponse} from '@Components/mailing-list-contacts/mailing-list-contacts.types';
import {mailingListPanelRefreshEvent, PMW_PRE_SELECTED_MAILING_LIST_ID} from '@Components/mailing-list-contacts/mailing-list-contacts.types';
import {
  decodeCookieValueForMailingListsSelection,
  isMailingListDeleteForbidden,
  showMailingListDeletionForbiddenDialog,
} from '@Components/mailing-list-contacts/mailing-list-contacts-library';
import {openAddMailingListModal, PANEL_ID} from '@Modals/add-mailing-list-modal';
import {AddMailingListStep} from '@Panels/add-mailing-list-panel/add-mailing-list-panel.types';
import type {CreateMailingListResponse} from '@Components/mailing-list-wizard/mailing-list-wizard.types';
import {CircularProgressLoader, LOADER_SIZE} from '@Components/circular-progress-loader';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {SelectableMailingList} from '@Components/email-maker-show-mailing-lists-panel/selectable-mailing-list';
import {escapeHTML} from '@Utils/string.util';
import type {AjaxResponse} from '@Utils/ajax.util';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';

export interface EmailMakerShowMailingListsPanelProps {
  idCampaign: string;
  onUpdateMailingListSelection: (mailingListIds: number[]) => void;
  onSpamInfoClick: () => void;
  onCreateMailingList: () => void;
  onDeleteMailingList: (mailingListId: number) => void;
  onEditMailingList: (mailingList: MailingList) => void;
}

export const EMAIL_MAKER_CONTAINER_QUERY: ContainerQueryObject = {
  small: {
    maxWidth: 456,
  },

  medium: {
    minWidth: 456,
    maxWidth: 680,
  },

  large: {
    minWidth: 1000,
  },
};

export type SelectedMailingListsMap = Record<number, boolean>;
export const triggerRefreshEvent = 'mailingList::update';

export enum EmailMakerShowMailingListsPanelSize {
  SMALL,
  MEDIUM,
  LARGE,
}

export default function EmailMakerShowMailingListsPanel({
  idCampaign,
  onUpdateMailingListSelection,
  onSpamInfoClick,
  onCreateMailingList,
  onDeleteMailingList,
  onEditMailingList,
}: EmailMakerShowMailingListsPanelProps): ReactElement<any> {
  const [userMailingLists, setUserMailingLists] = useState<MailingList[] | null>(null);
  const [selectedMailingListsMap, setSelectedMailingListsMap] = useState<SelectedMailingListsMap>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const containerQueryResult = useContainerQuery(containerRef, EMAIL_MAKER_CONTAINER_QUERY);
  const [isRefreshTriggered, setIsRefreshTriggered] = useState(true);

  const mailingListUpdateCallback = (selectedMailingLists: SelectedMailingListsMap): void => {
    const selectedMailingListIds = Object.entries(selectedMailingLists)
      .filter(([_, isSelected]) => isSelected)
      .map(([id]) => Number(id));

    onUpdateMailingListSelection(selectedMailingListIds);
  };

  const addMailingListToCampaign = async (idMailingList: number): Promise<void> => {
    await window.PMW.writeLocal('emailmarketing/addMailingListToEmailCampaign', {
      idMailingList: `${idMailingList}`,
      idEmailMarketingCampaign: idCampaign,
    });
  };

  useEffect(() => {
    window.addEventListener(triggerRefreshEvent, () => {
      setIsRefreshTriggered(true);
    });

    return (): void => {
      window.removeEventListener(triggerRefreshEvent, () => {
        setIsRefreshTriggered(false);
      });
    };
  }, []);

  const buildMailingListsSelectedMap = (data: WizardMailingListsFetchResponse): SelectedMailingListsMap => {
    const selectedMailingListsMap: SelectedMailingListsMap = {};
    const allMailingListsResponse = data.allMailingLists;
    const responseData = data.selectedMailingListsMap;

    allMailingListsResponse.forEach((item) => {
      const mailingListId = item.id;
      const doesCookieValueIncludeMailingListId = doesCookieValueExistForMailingListId(mailingListId);
      // @ts-ignore
      const isValidList = item.mailingListContacts !== undefined && item.mailingListContacts.length > 0;
      const isAlreadySelected = responseData[mailingListId] !== undefined && responseData[mailingListId];

      if ((isAlreadySelected && isValidList) || doesCookieValueIncludeMailingListId) {
        selectedMailingListsMap[item.id] = true;
      } else {
        selectedMailingListsMap[item.id] = false;
      }

      if (doesCookieValueIncludeMailingListId && !isAlreadySelected) {
        addMailingListToCampaign(mailingListId).catch((e): void => {
          selectedMailingListsMap[item.id] = false;
        });
      }
    });

    return selectedMailingListsMap;
  };

  useEffect(() => {
    mailingListUpdateCallback(selectedMailingListsMap);
  }, [selectedMailingListsMap]);

  useEffect(() => {
    if (!isRefreshTriggered) {
      return;
    }

    window.PMW.writeLocal(`emailmaker/getMailingListsInformationForUser`, {idCampaign: idCampaign})
      .then((data: WizardMailingListsFetchResponse) => {
        const selectedMailingListsMap: SelectedMailingListsMap = buildMailingListsSelectedMap(data);
        mailingListUpdateCallback(selectedMailingListsMap);
        setUserMailingLists(data.allMailingLists);
        setSelectedMailingListsMap(selectedMailingListsMap);
        invalidateMailingListSelectionCookieIfNeeded();

        if (isRefreshTriggered) {
          setIsRefreshTriggered(false);
        }
      })
      .catch((e): void => {
        console.error(e);
      });
  }, [isRefreshTriggered]);

  const onTriggerRefresh = (): void => {
    setIsRefreshTriggered(true);
  };

  useEffect(() => {
    window.addEventListener(mailingListPanelRefreshEvent, onTriggerRefresh, {passive: true});

    return (): void => {
      window.removeEventListener(mailingListPanelRefreshEvent, onTriggerRefresh);
    };
  }, []);

  const getDecodedMailingListIds = (): number[] => {
    const cookie = readCookie(PMW_PRE_SELECTED_MAILING_LIST_ID);

    if (!cookie) {
      return [];
    }

    return decodeCookieValueForMailingListsSelection(cookie);
  };

  const doesCookieValueExistForMailingListId = (mailingListId: number): boolean => {
    const decodedMailingListIdsFromCookie = getDecodedMailingListIds();
    return decodedMailingListIdsFromCookie.includes(mailingListId);
  };

  const invalidateMailingListSelectionCookieIfNeeded = (): void => {
    const cookie = readCookie(PMW_PRE_SELECTED_MAILING_LIST_ID);

    if (!cookie) {
      return;
    }

    deleteCookie(PMW_PRE_SELECTED_MAILING_LIST_ID);
  };

  const getHeading = (): ReactElement<any> => {
    return <Text className={'spacing-m-b-5 panel-title'} val={window.i18next.t('pmwjs_mailing_list_panel_header')} size={TextSize.XLARGE} bold />;
  };

  const fetchMailingListInformation = async (mailingListId: number): Promise<MailingListFetchResponse> => {
    return window.PMW.readLocal(`emailmarketing/getMailingListById`, {
      idMailingList: mailingListId,
    });
  };

  const addNewMailingListToState = (mailingList: MailingList): void => {
    if (!userMailingLists) {
      setUserMailingLists([mailingList]);
    } else {
      const storedUserMailingLists = [mailingList, ...userMailingLists];
      setUserMailingLists([...storedUserMailingLists]);
    }
  };

  const setMailingListSelectionState = (mailingList: MailingList): void => {
    setSelectedMailingListsMap((prev) => ({
      ...prev,
      [mailingList.id]: true,
    }));
  };

  const onAddAudienceSuccess = (response: CreateMailingListResponse): void => {
    const mailingListIdAdded = response.idMailingList;

    if (!mailingListIdAdded) {
      return;
    }

    fetchMailingListInformation(mailingListIdAdded)
      .then((data: MailingListFetchResponse) => {
        const mailingList = data.mailingList;
        addNewMailingListToState(mailingList);
        onCreateMailingList();

        if (mailingList.mailingListContacts === undefined || mailingList.mailingListContacts.length === 0) {
          return;
        }

        setMailingListSelectionState(mailingList);
      })
      .catch((e: any) => {
        console.error('Error:', e);
      });
  };

  const onClickAddNewMailingList = (): void => {
    openAddMailingListModal({
      panelId: PANEL_ID,
      initialStep: AddMailingListStep.CHOOSE_UPLOAD_METHOD,
      idEmailMarketingCampaign: idCampaign,
      onAddAudienceSuccess: onAddAudienceSuccess,
    });
  };

  const getAddNewButton = (): ReactElement<any> => {
    return (
      <ClickableDiv onClick={onClickAddNewMailingList} className={`${styles.addNewWrapper} ${getStylesBySize()}`}>
        <div className={`${styles.addButton} flex-h-row flex-center spacing-m-t-1 spacing-m-r-5 spacing-m-l-1 spacing-m-b-1`}>
          <Icon icon="icon-plus" size={IconSize.SIZE_ICON_20} type={IconType.FLAT} flatIconType={{color: FlatIconColor.NEUTRAL, type: FlatIconColorType.DEFAULT}} />
        </div>
        <Text className={'content-sub-text'} val={window.i18next.t('pmwjs_add_new_mailing_list')} size={TextSize.XSMALL} />
      </ClickableDiv>
    );
  };

  const isMailingListSelected = (mailingList: MailingList): boolean => {
    if (selectedMailingListsMap[mailingList.id] !== undefined && selectedMailingListsMap[mailingList.id]) {
      return true;
    }

    return false;
  };

  const isMailingListDisabled = (mailingList: MailingList): boolean => {
    if (mailingList.numEmails === undefined || mailingList.numEmails === 0 || mailingList.mailingListContacts === undefined || mailingList.mailingListContacts.length === 0) {
      return true;
    }

    return false;
  };

  const onCheckToggle = (mailingList: MailingList): void => {
    if (isMailingListDisabled(mailingList) || mailingList.isSpam) {
      return;
    }
    const mailingListId = mailingList.id;
    const wasSelected = selectedMailingListsMap[mailingListId] ?? false;
    const willBeSelected = !wasSelected;

    setSelectedMailingListsMap((prev) => ({
      ...prev,
      [mailingListId]: willBeSelected,
    }));

    if (willBeSelected && mailingList.isSmart) {
      window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EMAILMAKER_SMARTMAILINGLIST, {[GA4EventParamName.TYPE]: mailingList.name});
    }
  };

  const onClickEdit = (mailingList: MailingList): void => {
    onEditMailingList(mailingList);
  };

  const onClickDelete = (mailingList: MailingList): void => {
    const mailingListId = mailingList.id;

    if (!confirm(window.i18next.t('pmwjs_delete_mailinglist_confirmation', {name: escapeHTML(mailingList.name)}))) {
      return;
    }

    void window.PMW.writeLocal('emailmarketing/deleteMailingList', {
      idMailingList: mailingListId,
    })
      .then(() => {
        setUserMailingLists((prev) => prev?.filter((ml) => ml.id !== mailingListId) ?? null);
        setSelectedMailingListsMap((prev) => {
          const {[mailingListId]: _, ...rest} = prev;
          return rest;
        });
        onDeleteMailingList(mailingListId);
      })
      .catch((r: unknown) => {
        if (isMailingListDeleteForbidden(r as AjaxResponse)) {
          showMailingListDeletionForbiddenDialog();
          return;
        }
      });
  };

  const getSize = (): EmailMakerShowMailingListsPanelSize => {
    if (containerQueryResult.medium) {
      return EmailMakerShowMailingListsPanelSize.MEDIUM;
    }

    if (containerQueryResult.small) {
      return EmailMakerShowMailingListsPanelSize.SMALL;
    }

    return EmailMakerShowMailingListsPanelSize.LARGE;
  };

  const getMailingList = (mailingList: MailingList): ReactElement<any> => {
    return (
      <SelectableMailingList
        mailingList={mailingList}
        key={`selectable-mailing-list-${mailingList.id}`}
        onCheckToggle={(mailingList) => {
          onCheckToggle(mailingList);
        }}
        isMailingListSelected={isMailingListSelected(mailingList)}
        isMailingListDisabled={isMailingListDisabled(mailingList)}
        onSpamInfoClick={onSpamInfoClick}
        onClickDelete={(mailingList) => {
          onClickDelete(mailingList);
        }}
        onClickEdit={(mailingList) => {
          onClickEdit(mailingList);
        }}
        size={getSize()}
      />
    );
  };

  const getMailingListTooltip = (mailingList: MailingList): ReactElement<any> => {
    if (mailingList.mailingListContacts && mailingList.mailingListContacts.length > 0) {
      return getMailingList(mailingList);
    }

    return (
      <OverlayTrigger
        key={`overlay-mailing-list-${mailingList.id}`}
        trigger={['hover', 'focus']}
        placement="top"
        delay={{show: 100, hide: 100}}
        overlay={<Tooltip className="spacing-m-b-1">{window.i18next.t('pmwjs_no_contacts_descriptive_tooltip')}</Tooltip>}
      >
        {getMailingList(mailingList)}
      </OverlayTrigger>
    );
  };

  const getAllMailingLists = (): ReactElement<any> => {
    if (!userMailingLists) {
      return <></>;
    }

    const mailingLists: ReactElement<any>[] = [];

    userMailingLists.forEach((item) => {
      mailingLists.push(getMailingListTooltip(item));
    });

    return <div className={`${styles.mailingListsPanelWrapper} js-email-campaign-required`}>{mailingLists}</div>;
  };

  const getStylesBySize = (): string => {
    if (containerQueryResult.medium) {
      return styles.medium;
    }

    if (containerQueryResult.small) {
      return styles.small;
    }

    return '';
  };

  const getLoader = (): ReactElement<any> => {
    return <CircularProgressLoader size={LOADER_SIZE.LARGE} classes={styles.loaderClasses} />;
  };

  const shouldShowLoader = (): boolean => {
    if (isRefreshTriggered || userMailingLists === null) {
      return true;
    }

    return false;
  };

  const getLoaderIfNeeded = (): ReactElement<any> | null => {
    if (shouldShowLoader()) {
      return <div className={'input-items'}>{getLoader()}</div>;
    }

    return null;
  };

  const getPanel = (): ReactElement<any> | null => {
    if (shouldShowLoader()) {
      return null;
    }

    return (
      <>
        {getHeading()}
        <div className={'input-items'}>
          {getAddNewButton()} {getAllMailingLists()}
        </div>
      </>
    );
  };

  return (
    <div ref={containerRef} className={styles.panelWrapper}>
      <>
        {getLoaderIfNeeded()}
        {getPanel()}
      </>
    </div>
  );
}
